import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Divider,
  Button,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import Breadcrumb from '../../../Components/BreadCrumb';
import { useCreateRoleMutation, useLazyGetRolePermissionByRoleIdQuery, useUpdateRoleMutation } from '../../../Services/RolesService';
import Utils from '../../../Helpers/Utils';
import { useNavigate, useParams } from "react-router-dom";
import RolesSkeleton from './RolesSkeleton';
import { useConfirmation } from '../../../Components/ConfirmationDialog';
import { showSnackbar } from '../../../Redux/Slices/NotificationSlice';
import { useDispatch } from 'react-redux';

/** Interface for Permission structure */
interface Permission {
  /** Modules with no write actions render only a View checkbox. */
  viewOnly?: boolean;
  id: string;
  label: string;
}

/** Interface for form input structure */
interface RoleFormInputs {
  roleName: string;
  permissions: Record<string, Set<string>>;
}

/** Interface for custom field error structure */
interface CustomFieldError {
  message?: string;
  type?: string;
}

const AddRoles = () => {
  /** State management hooks */
  const [errorMsg, setErrorMsg] = useState({ msg: "", type: "" });
  const [selectAll, setSelectAll] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, Set<string>>>({});
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  /** Breadcrumb configuration */
  const breadcrumbItems = [
    // { label: "Dashboard", path: "dashboard" },
    { label: "Roles", path: "roles" },
    { label: `${(id ? "Update" : "Submit")} Role` },
  ];

  /** Form control initialization */
  const { control, handleSubmit, formState: { errors , isDirty}, setError, clearErrors, setValue } = useForm<RoleFormInputs>({
    defaultValues: {
      roleName: '',
      permissions: {}
    }
  });

  /** RTK Query mutations */
  const [createRole, { isLoading, isSuccess }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdateLoading, isSuccess: isUpdateSuccess }] = useUpdateRoleMutation();
  const [trigger, { isLoading: isPermissionLoading, isFetching }] = useLazyGetRolePermissionByRoleIdQuery();
  const { requestConfirmation } = useConfirmation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const dispatch = useDispatch();

  /** Available permissions configuration */
  const permissions: Permission[] = Utils.permissions;

  /** Available actions for permissions */
  const actions: string[] = ['View', 'Edit'];

  /** Effect to fetch role data when editing */
  useEffect(() => {
    if (id) {
      getRoleDataByRoleId(id);
    }
  }, [id]);

  /** 
   * Fetches and sets role data for editing
   * @param roleId - The ID of the role to fetch
   */
  const getRoleDataByRoleId = async (roleId: string) => {
    const result = await trigger(roleId).unwrap();
    // console.log(JSON.stringify(result), 'called api');


    if (result && result?._id) {
      const { roleName, permissions } = result;

      /** Convert permissions from array format to Set format */
      const formattedPermissions = Utils.reverseToArrayFormatRole({ ...result }).permissions;

      /** Update form values */
      setValue('roleName', roleName);

      /** Convert permissions to Set format */
      const permissionsWithSets = Object.fromEntries(
        Object.entries(formattedPermissions).map(([key, actions]: any) => [
          key,
          new Set(actions),
        ])
      );

      /** Update states */
      setSelectedPermissions(permissionsWithSets);
      setValue('permissions', formattedPermissions);

      /** Update selectAll state based on permissions */
      const allSelected = Object.values(permissionsWithSets).every((set: any) =>
        actions.length === set.size && actions.every(action =>
          set.has(action.toLowerCase())
        )
      );
      setSelectAll(allSelected);
    }
  };

  /** Effect to handle navigation after successful submission */
  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      navigate('/roles', { replace: true })
    }
  }, [isSuccess, isUpdateSuccess]);

  /** 
   * Handles the select all checkbox change
   * @param event - The checkbox change event
   */
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectAll(checked);

    /** Create new permissions object with all permissions either selected or deselected */
    const newPermissions: Record<string, Set<string>> = {};
    permissions.forEach(permission => {
      newPermissions[permission.id] = new Set(checked ? actions.map(a => a.toLowerCase()) : []);
    });
    setSelectedPermissions(newPermissions);
    setHasUnsavedChanges(true); 
  };

  /** 
   * Handles individual permission checkbox changes
   * @param permissionId - The ID of the permission being changed
   * @param type - The type of permission (view, edit, etc.)
   * @param checked - The new checked state
   */
   const handlePermissionChange = (permissionId: string, type: string, checked: boolean) => {
    setSelectedPermissions(prev => {
      const newPermissions = { ...prev };
      if (!newPermissions[permissionId]) {
        newPermissions[permissionId] = new Set();
      }
      const permissionSet = new Set(newPermissions[permissionId]);

      /** Add or remove permission based on checkbox state */
      if (checked) {
        permissionSet.add(type);
      } else {
        permissionSet.delete(type);
      }
      newPermissions[permissionId] = permissionSet;

      /** Update selectAll state based on all permissions */
      const allSelected = Object.values(newPermissions).every(set =>
        actions.length === set.size && actions.every(action =>
          set.has(action.toLowerCase())
        )
      );
      setSelectAll(allSelected);
      setHasUnsavedChanges(true); // ✅ Mark as changed

      return newPermissions;
    });
  };

  /** Validates that at least one permission is selected */
  const validatePermissions = () => {
    const hasAnyPermission = Object.values(selectedPermissions).some(permissionSet =>
      permissionSet.size > 0
    );

    if (!hasAnyPermission) {
      setError('permissions', {
        type: 'manual',
        message: 'Please select at least one permission'
      });
      return false;
    }

    clearErrors('permissions');
    return true;
  };


  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
  
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);
  

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      requestConfirmation({
        title: 'Unsaved changes detected',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        onConfirm: async () => {
          navigate('/roles', { replace: true });
        },
        onCancel: () => {},
      });
    } else {
      navigate('/roles', { replace: true });
    }
  };
  


  /** 
   * Handles form submission
   * Validates permissions and submits to appropriate API endpoint
   */
  const onSubmit = async (data: RoleFormInputs) => {
    if (!validatePermissions()) {
      return;
    }

    /** Format data for API submission */
    const formattedData = {
      roleName: data.roleName,
      permissions: Object.entries(selectedPermissions).reduce((acc, [key, value]) => {
        if (value.size > 0) {
          acc[key] = Array.from(value);
        }
        return acc;
      }, {} as Record<string, string[]>)
    };

    // Submit to API based on whether we're creating or updating
    try {
      if (id) {
        const result: any = await updateRole({ roleId: id, reqBody: Utils.convertToBooleanFormatRoleOfPermissions(formattedData) });
        // console.log(result, 'result1');
    
        if (result?.error?.data) {
          setErrorMsg({ msg: result.error.data.message, type: "error" });
    
          dispatch(showSnackbar({
            message: result.error.data.message || 'Failed to save role',
            severity: 'error'
          }));
        } else {
          dispatch(showSnackbar({
            message: 'Role updated successfully!',
            severity: 'success'
          }));
        }
    
      } else {
        const result: any = await createRole(Utils.convertToBooleanFormatRole(formattedData));

        if (result?.error?.data) {
          setErrorMsg({ msg: result.error.data.message, type: "error" });
    
          dispatch(showSnackbar({
            message: result.error.data.message || 'Failed to save role',
            severity: 'error'
          }));
        } else {
          dispatch(showSnackbar({
            message: 'Role created successfully!',
            severity: 'success'
          }));
        }
      }
    } catch (err: any) {
      dispatch(showSnackbar({
        message: err?.data?.message || 'Failed to save role',
        severity: 'error'
      }));
    }    
    setHasUnsavedChanges(false);
  };



  // 1. Constants for reserved role names
  const RESERVED_ROLE_NAMES = ['Super Admin', 'superAdmin', 'SuperAdmin', 'Administrator', 'administrator', 'System'];

  /**
 * 2. Validation function for role name
 * @param {string} roleName - The role name to validate
 * @returns {string|null} - Error message or null if valid
 */
  const validateRoleName = (roleName: any) => {
    if (!roleName) {
      return 'Role name is required';
    }

    if (RESERVED_ROLE_NAMES.some(name =>
      name.toLowerCase() === roleName.trim().toLowerCase())) {
      return `"${roleName}" is a reserved role name and cannot be used`;
    }

    return true; // Valid role name
  };

  return (
    <Box>
      <div style={{ flexGrow: 1 }}>
        <Breadcrumb items={breadcrumbItems} name={'Roles'} />
      </div>
      {isPermissionLoading || isFetching ? <RolesSkeleton /> : (
        <Paper elevation={2} sx={{
          padding: 3,
          mt: 2,
          background: theme.palette.common.white,
          borderRadius: 2,
          [theme.breakpoints.down('sm')]: {
            padding: (theme) => theme.spacing(1),
          },
        }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="roleName"
              control={control}
              rules={{
                required: 'Role name is required',
                maxLength: { value: 50, message: 'Role name must be at most 50 characters' },
                minLength: { value: 3, message: 'Role name must be at least 3 characters' },
                pattern: {
                  value: /^[a-zA-Z0-9\s-_]+$/,
                  message: 'Role name can only contain letters, numbers, spaces, hyphens, and underscores'
                },
                validate: validateRoleName
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Role Name"
                  variant="outlined"
                  margin="normal"
                  disabled={id ? true : false}
                  error={!!errors.roleName}
                  helperText={errors.roleName?.message}
                  placeholder="Enter role name"
                />
              )}
            />

            <Box sx={{ mt: 1, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '15px', sm: '16px', md: '17px' }, fontWeight: '700' }} >
                Role Permissions
              </Typography>
              {/* <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                }
                label="Select All"
              /> */}
            </Box>

            <Divider />

            <Box>
              {permissions.map((permission) => (
                <Box key={permission.id} sx={{ py: 1, borderBottom: '1px solid #e0e0e0' }}>
                  <Grid container alignItems="center">
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography sx={{ fontSize: { xs: '15px', sm: '16px', md: '16px' } }}>{permission.label}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <FormGroup row>
                        {(permission.viewOnly ? actions.filter((a) => a.toLowerCase() === "view") : actions).map((type) => (
                          <FormControlLabel
                            key={type}
                            control={
                              <Checkbox
                                checked={selectedPermissions[permission.id]?.has(type.toLowerCase()) ? true : false}
                                onChange={(e) => handlePermissionChange(permission.id, type.toLowerCase(), e.target.checked)}
                              />
                            }
                            label={type}
                            sx={{ fontSize: { xs: '15px', sm: '42px', md: '30px' }, borderRadius: 50 }}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                mt: 3,
                mb: 2,
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{width: '100%'}}>
                <Typography
                  sx={{
                    visibility: (errors?.permissions as CustomFieldError)?.message ? 'visible' : 'hidden',
                    color: 'error.main',
                    fontSize: { xs: '10px', sm: '11px', md: '12px' },
                    fontWeight: '400',
                    minWidth: { xs: '100%', sm: 'auto', }
                  }}
                >{(errors?.permissions as CustomFieldError)?.message || 'Please select at least one permission'}</Typography>

                <Typography
                  sx={{
                    visibility: errorMsg?.msg ? 'visible' : 'hidden',
                    color: 'error.main',
                    fontSize: { xs: '10px', sm: '11px', md: '12px' },
                    fontWeight: '400',
                    minWidth: { xs: '100%', sm: 'auto', }
                  }}
                >{errorMsg?.msg}</Typography>
              </div>

              <Box sx={{
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                minWidth: { xs: '100%', sm: 'auto' }
              }}>
                <Button
                  type="button"
                  variant={'outlined'}
                  color="error"
                  onClick={handleCancel}
                  fullWidth
                  sx={{ minWidth: { sm: '120px' }, height: 38 }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant={'contained'}
                  color='primary'
                  fullWidth
                  sx={{ minWidth: { sm: '120px' }, height: 38 }}
                  disabled={(isLoading || isUpdateLoading)}
                >
                  {(isLoading || isUpdateLoading) ? <CircularProgress size={24} /> : (id ? "Update" : "Submit")}
                </Button>


              </Box>
            </Stack>
          </form>
        </Paper>
      )}

    </Box>
  );
}

export default AddRoles;
